import {
  BuiltInNumericProperty,
  Measure,
  MeasureOperator,
  Metric,
} from '@fullstory/proto/fs/api/metrics';
import {
  Display,
  DisplayDurationUnit,
  DisplayPrecision,
} from '@fullstory/proto/fs/services/webber/frontend/display';
import { isEqual } from 'lodash';
import {
  DEFAULT_DISPLAY,
  DEFAULT_DUAL_DIVIDE_DISPLAY,
  PERCENT_UNMODIFIED_DISPLAY,
} from '../constants';

const AUTOSCALE_DEFAULT = true;

const isClsAggProperty = (measure: Measure | null | undefined): boolean =>
  measure?.aggregation?.numeric?.property?.builtInProperty ===
  BuiltInNumericProperty.NUMERIC_PROPERTY_CUMULATIVE_LAYOUT_SHIFT;

export const knownUnit = <T>(
  metric: Metric | null | undefined,
  check: (measure: Measure | null | undefined) => T,
): T | null => {
  if (metric?.single) {
    return check(metric.single);
  }

  if (metric?.dual) {
    const firstUnit = check(metric.dual.first);
    const secondUnit = check(metric.dual.second);
    const addOrSubtract =
      metric.dual.operator === MeasureOperator.MEASURE_OPERATOR_ADD ||
      metric.dual.operator === MeasureOperator.MEASURE_OPERATOR_SUBTRACT;

    // if both the first and second unit are equal, AND it's add or subtract, return the known unit.
    // for the multiply/divide, allow someone to change the display format
    if (firstUnit === secondUnit && addOrSubtract) {
      return firstUnit;
    }
  }

  return check(null);
};

const numericPropertyUnits: { [property: number]: DisplayDurationUnit } = {
  [BuiltInNumericProperty.NUMERIC_PROPERTY_PAGE_DURATION]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_PAGE_ACTIVE_DURATION]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_SINCE_PAGE_LOAD]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_SINCE_SESSION_START]:
    DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_LOAD_EVENT_DURATION]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_DOM_CONTENT_DURATION]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_FIRST_MEANINGFUL_PAINT_DURATION]:
    DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_DURATION_RENDERED]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_DURATION_VISIBLE]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_PAGEVIEW_TOTAL_DURATION]:
    DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_PAGEVIEW_ACTIVE_DURATION]:
    DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_PAGEVIEW_INACTIVE_DURATION]:
    DisplayDurationUnit.DURATION_MS,

  // web vitals
  [BuiltInNumericProperty.NUMERIC_PROPERTY_FIRST_INPUT_DELAY]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_LARGEST_CONTENTFUL_PAINT_DURATION]:
    DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_INTERACTION_TO_NEXT_PAINT]:
    DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_TO_INTERACTIVE]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TOTAL_BLOCKING_TIME]: DisplayDurationUnit.DURATION_MS,
  [BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_TO_FIRST_BYTE]: DisplayDurationUnit.DURATION_MS,
};

/**
 * Gets the well-known (to FullStory) duration unit for the metric, if any.
 *
 * Example: page load events are recorded in milliseconds,
 * so this function returns DurationUnit.DURATION_MS
 * for a metric measuring page load.
 */
const durationUnitForMeasure = (
  measure: Measure | null | undefined,
): DisplayDurationUnit | null => {
  const property = measure?.aggregation?.numeric?.property?.builtInProperty;
  if (property && property in numericPropertyUnits) {
    // We could type `numericPropertyUnits` to be a mapped type with the enum as
    // keys instead of a number to avoid the non-null assertion but there are a
    // few BuiltInNumericProperties that do not map to a DisplayDurationUnit.
    // Using the non-null for now since we are checking the property exists in
    // the above conditional.
    return numericPropertyUnits[property]!;
  }
  return null;
};

export const getKnownClsUnit = (metric: Metric | null | undefined): boolean | null =>
  knownUnit(metric, isClsAggProperty);

export const getKnownDurationUnit = (
  metric: Metric | null | undefined,
): DisplayDurationUnit | null => knownUnit(metric, durationUnitForMeasure);

/**
 * Gets the well-known (to FullStory) percent unmodified units for the metric, if any.
 *
 * Example: Scroll depth is a percentage recorded as an integer (e.g. 98 for 98%). Hence, it doesn't need to be
 * converted from a float (e.g. 0.98) to a whole number (e.g. 98%).
 */
const isPercentUnmodifiedUnit = (measure: Measure | null | undefined): boolean =>
  measure?.aggregation?.numeric?.property?.builtInProperty ===
  BuiltInNumericProperty.NUMERIC_PROPERTY_SCROLL_DEPTH;

export const getKnownPercentUnmodifiedUnit = (metric: Metric | null | undefined): boolean | null =>
  knownUnit(metric, isPercentUnmodifiedUnit);

export const getKnownUnitsForDisplay = (
  metric: Metric,
): {
  knownPercentUnmodifiedUnit: boolean | null;
  knownDurationUnit: DisplayDurationUnit | null;
  knownClsUnit: boolean | null;
} => {
  const knownPercentUnmodifiedUnit = getKnownPercentUnmodifiedUnit(metric);
  const knownDurationUnit = getKnownDurationUnit(metric);
  const knownClsUnit = getKnownClsUnit(metric);
  return { knownPercentUnmodifiedUnit, knownDurationUnit, knownClsUnit };
};

/**
 * Default dual metrics with a division operator to a percentage display with zero precision. Default all other dual metrics
 * and single metrics to the default display. Certain properties also set the display.
 */
export const getDisplayForMetric = (
  currentDisplay: Display,
  metric: Metric,
  passThroughFormat: boolean,
): Display => {
  const knownUnits = getKnownUnitsForDisplay(metric);
  const { knownPercentUnmodifiedUnit, knownDurationUnit, knownClsUnit } = knownUnits;

  if (knownDurationUnit) {
    return {
      displayPrecision: DisplayPrecision.PRECISION_0,
      displayDuration: {
        unit: knownDurationUnit,
        autoscale: currentDisplay?.displayDuration?.autoscale ?? AUTOSCALE_DEFAULT,
      },
    };
  }

  if (knownPercentUnmodifiedUnit) {
    return PERCENT_UNMODIFIED_DISPLAY;
  }

  if (knownClsUnit) {
    return {
      number: true,
      displayPrecision: DisplayPrecision.PRECISION_2,
    };
  }

  if (metric.dual && metric.dual.operator == MeasureOperator.MEASURE_OPERATOR_DIVIDE) {
    if (
      (isEqual(currentDisplay, DEFAULT_DISPLAY) || !currentDisplay.percent) &&
      !passThroughFormat
    ) {
      return DEFAULT_DUAL_DIVIDE_DISPLAY;
    } else {
      return currentDisplay;
    }
  }

  if (
    (metric.single ||
      (metric.dual && metric.dual.operator !== MeasureOperator.MEASURE_OPERATOR_DIVIDE)) &&
    isEqual(currentDisplay, DEFAULT_DUAL_DIVIDE_DISPLAY)
  ) {
    return DEFAULT_DISPLAY;
  }

  if (passThroughFormat) {
    return currentDisplay;
  } else {
    return DEFAULT_DISPLAY;
  }
};
